import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Bell as BellIcon, Check as CheckIcon } from "@air/next-icons";
import { useToasts } from "@air/provider-toast";
import Link from "next/link";
import { useCallback } from "react";
import { Routes } from "~/constants/routes";
import { saveTurnOnNotificationsPromptShown, wasTurnOnNotificationsPromptShown } from "~/hooks/turnOnEmailShareNotificationsToast/turnOnNotificationsPromptUtils";
import { useAccountContext } from "~/providers/AccountProvider";
import { useCurrentWorkspace } from "~/providers/CurrentWorkspaceProvider";
import { useNotificationSettings } from "~/swr-hooks/account/useNotificationSettings";
import { useUpdateNotificationSettings } from "~/swr-hooks/account/useUpdateNotificationSettings";
export const useShowTurnOnShareLinkNotifications = ()=>{
    const { data: account } = useAccountContext();
    const { currentWorkspace } = useCurrentWorkspace();
    const { data: notificationSettings } = useNotificationSettings();
    const { updateNotificationSettings } = useUpdateNotificationSettings();
    const { showToast, dismissAllToasts } = useToasts();
    const showTurnOnShareLinkNotifications = useCallback((param)=>{
        let { onManageClick } = param;
        if (!account || !currentWorkspace) {
            return;
        }
        const areSettingsAlreadyEnabled = !!(notificationSettings === null || notificationSettings === void 0 ? void 0 : notificationSettings.emails.shareLinkViews) && !!(notificationSettings === null || notificationSettings === void 0 ? void 0 : notificationSettings.emails.shareLinkDownloads);
        // user already has setting enabled - no need to show toast
        if (areSettingsAlreadyEnabled) {
            return;
        }
        const wasPromptShown = wasTurnOnNotificationsPromptShown({
            email: account === null || account === void 0 ? void 0 : account.email,
            workspaceId: currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.id
        });
        if (wasPromptShown) {
            return;
        }
        const showSuccessToast = ()=>{
            showToast(/*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    "You will now receive emails about new activity on share links you create",
                    " ",
                    /*#__PURE__*/ _jsx(Link, {
                        href: Routes.account.notificationSettings,
                        onClick: ()=>onManageClick(),
                        className: "ml-2 text-14 font-semibold text-pigeon-25 hover:underline",
                        children: "Manage"
                    })
                ]
            }), {
                prefix: /*#__PURE__*/ _jsx(CheckIcon, {
                    className: "size-4 text-parrot-400"
                })
            });
        };
        const turnOnNotifications = async ()=>{
            try {
                await updateNotificationSettings.mutateAsync({
                    emails: {
                        shareLinkViews: true,
                        shareLinkDownloads: true
                    }
                });
                showSuccessToast();
            } catch (_error) {
                showToast(/*#__PURE__*/ _jsx(_Fragment, {
                    children: "Something went wrong. Please try again later."
                }), {
                    color: "red"
                });
            }
        };
        showToast(/*#__PURE__*/ _jsxs("div", {
            className: "flex gap-2",
            children: [
                "Turn on email notifications for share links you create",
                /*#__PURE__*/ _jsx("button", {
                    onClick: ()=>{
                        dismissAllToasts();
                        turnOnNotifications();
                    },
                    className: "text-14 font-semibold text-grey-5 hover:underline",
                    children: "Turn on"
                })
            ]
        }), {
            prefix: /*#__PURE__*/ _jsx(BellIcon, {
                className: "size-4"
            }),
            withCloseButton: true
        });
        saveTurnOnNotificationsPromptShown({
            email: account === null || account === void 0 ? void 0 : account.email,
            workspaceId: currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.id
        });
    }, [
        account,
        currentWorkspace,
        dismissAllToasts,
        notificationSettings === null || notificationSettings === void 0 ? void 0 : notificationSettings.emails.shareLinkDownloads,
        notificationSettings === null || notificationSettings === void 0 ? void 0 : notificationSettings.emails.shareLinkViews,
        showToast,
        updateNotificationSettings
    ]);
    return {
        showTurnOnShareLinkNotifications
    };
};
